import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

interface AxiosCall<T = any> {
  controller?: AbortController;
  call: () => Promise<{ data: T; status: number }>;
}

interface CallEndpointResult<T = any> {
  data: T | null;
  status: number | null;
}

const useFetchAndLoad = () => {
  const [loading, setLoading] = useState(false);
  const controllersRef = useRef<Set<AbortController>>(new Set());
  const isMountedRef = useRef(true);

  const callEndpoint = useCallback(
    async <T = any>(axiosCall: AxiosCall<T>): Promise<CallEndpointResult<T>> => {
      let controller: AbortController | null = null;

      if (axiosCall.controller) {
        controller = axiosCall.controller;
        controllersRef.current.add(controller);
      }

      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        const result = await axiosCall.call();

        if (isMountedRef.current) {
          setLoading(false);
        }

        if (controller) {
          controllersRef.current.delete(controller);
        }

        return result;
      } catch (err: any) {
        if (isMountedRef.current) {
          setLoading(false);
        }
        if (controller) {
          controllersRef.current.delete(controller);
        }

        if (axios.isCancel(err) || err.code === 'ERR_CANCELED') {
          return { data: null, status: null };
        }

        throw err;
      }
    },
    [],
  );

  const cancelAllEndpoints = useCallback(() => {
    controllersRef.current.forEach((controller) => {
      controller.abort();
    });
    controllersRef.current.clear();

    if (isMountedRef.current) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cancelAllEndpoints();
    };
  }, [cancelAllEndpoints]);

  return { loading, callEndpoint, cancelAllEndpoints };
};

export default useFetchAndLoad;
